import type { VersionSummary } from "../../agreement";

export type MajorVersionGroup = {
  major: VersionSummary;
  revisions: VersionSummary[];
};

export function groupVersionsByMajor(versions: VersionSummary[]): MajorVersionGroup[] {
  const majors = versions
    .filter((v) => v.is_major)
    .sort((a, b) => b.version - a.version);

  const revisionsByMajor = new Map<number, VersionSummary[]>();
  for (const v of versions) {
    if (v.is_major) continue;
    const list = revisionsByMajor.get(v.version) ?? [];
    list.push(v);
    revisionsByMajor.set(v.version, list);
  }

  for (const list of Array.from(revisionsByMajor.values())) {
    list.sort((a, b) => (a.revision_index ?? 0) - (b.revision_index ?? 0));
  }

  return majors.map((major) => ({
    major,
    revisions: revisionsByMajor.get(major.version) ?? [],
  }));
}

export function countActiveRevisions(versions: VersionSummary[]): number {
  return versions.filter((v) => v.is_promotable).length;
}

export function revisionRowLabel(v: VersionSummary): string {
  if (v.revision_index != null) return `Rev ${v.revision_index}`;
  return v.display_label || `v${v.version}`;
}

export function lineageLabel(v: VersionSummary): string | null {
  if (!v.parent_display_label) return null;
  return `forked from ${v.parent_display_label}`;
}

export function sharePointUrlForVersion(v: VersionSummary): string | null {
  const sharing = v.sharepoint_sharing_url?.trim();
  if (sharing) return sharing;
  const web = v.sharepoint_web_url?.trim();
  if (web) return web;
  return null;
}

/** Compact date for major rows — "Jul 2, 2026 · 16:40" */
export function formatVersionDateCompact(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const month = d.toLocaleString(undefined, { month: "short" });
  const day = d.getDate();
  const year = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${month} ${day}, ${year} · ${hh}:${mm}`;
}

/** Locale datetime for revision rows */
export function formatVersionDateLocale(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}
